import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setActiveCompanyId, getActiveCompanyId } from "./api";

const AuthCtx = createContext({});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon
  const [companies, setCompanies] = useState([]);
  const [activeCompany, setActiveCompany] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setCompanies(data.companies || []);
      // pick active company
      const cid = getActiveCompanyId();
      const userCompanies = data.companies || [];
      let active = userCompanies.find((c) => c.id === cid) || userCompanies[0] || null;
      if (active) {
        setActiveCompanyId(active.id);
        setActiveCompany(active);
      } else {
        // No company memberships at all OR stored id is stale and there's
        // no fallback — wipe localStorage so we don't keep sending a bad header.
        setActiveCompanyId(null);
        setActiveCompany(null);
      }
      return { user: data.user, companies: userCompanies, activeCompany: active };
    } catch (e) {
      setUser(false);
      setCompanies([]);
      setActiveCompany(null);
      setActiveCompanyId(null);
      return { user: false, companies: [], activeCompany: null };
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    // Clear any stale company id from a previous session before we attach
    // the cookie — prevents "Not a member of this company" 403s when the
    // first post-login API call races with /auth/me.
    setActiveCompanyId(null);
    setActiveCompany(null);
    await api.post("/auth/login", { email, password });
    await refresh();
  };

  const register = async (payload) => {
    setActiveCompanyId(null);
    setActiveCompany(null);
    const { data } = await api.post("/auth/register", payload);
    if (data.company?.id) setActiveCompanyId(data.company.id);
    await refresh();
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); }
    catch (e) { console.warn("logout request failed", e?.response?.status || e?.message); }
    setActiveCompanyId(null);
    setUser(false);
    setCompanies([]);
    setActiveCompany(null);
  };

  const switchCompany = (companyId) => {
    setActiveCompanyId(companyId);
    const c = companies.find((c) => c.id === companyId);
    setActiveCompany(c || null);
  };

  return (
    <AuthCtx.Provider value={{ user, companies, activeCompany, refresh, login, register, logout, switchCompany }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
